import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRole } from "@/lib/useRole";

const ROLE_LABEL = { analyst: "Analyst", scientific_reviewer: "Scientific Reviewer", senior_reviewer: "Senior Reviewer", admin: "Administrator" };
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { FileText, Download, ExternalLink, CheckCircle2, Clock, AlertTriangle, Loader2 } from "lucide-react";

const TABS = ["Overview", "Document", "Extracted Claims", "HCP Statements", "Evidence Assessment", "Relationships", "Review", "Audit History"];

export const VERIF_BG = {
  "Source verified": "bg-teal-50 text-teal-700 border-teal-200",
  "Partially verified": "bg-amber-50 text-amber-700 border-amber-200",
  "Verification pending": "bg-amber-50 text-amber-700 border-amber-200",
  "Not started": "bg-slate-50 text-slate-500 border-slate-200",
  "Verification failed": "bg-red-50 text-red-700 border-red-200",
  Superseded: "bg-slate-100 text-slate-500 border-slate-300",
};
export const SCIREV_BG = {
  "Approved for use in synthesis": "bg-teal-50 text-teal-700 border-teal-200",
  "Scientifically reviewed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Under scientific review": "bg-sky-50 text-sky-700 border-sky-200",
  "Revision required": "bg-amber-50 text-amber-700 border-amber-200",
  "Not reviewed": "bg-slate-50 text-slate-500 border-slate-200",
  "Excluded from synthesis": "bg-red-50 text-red-700 border-red-200",
};

const Field = ({ l, v }) => (
  <div className="min-w-0">
    <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{l}</p>
    <p className="text-[11.5px] text-slate-700 break-words">{v ?? "—"}</p>
  </div>
);

function QuarantineBlock({ s, onUpdated }) {
  const [cmp, setCmp] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/source-library/${s.id}/conflict`).then((r) => setCmp(r.data)).catch(() => {}); }, [s.id, s.verification_status]);
  const resolve = () => {
    if (reason.trim().length < 5) { toast.error("A resolution reason is required."); return; }
    setBusy(true);
    api.post(`/source-library/${s.id}/resolve-conflict`, { reason })
      .then((r) => {
        toast.success(r.data.resolved ? "Conflict resolved — authoritative values applied, source re-verified." : "Authoritative values applied — validation still failing.");
        api.get(`/source-library/${s.id}/conflict`).then((x) => setCmp(x.data));
        onUpdated({ ...s, verification_status: r.data.validation.resulting_status, quarantine: { active: false } });
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Resolution failed — please retry.", { action: { label: "Retry", onClick: resolve } }))
      .finally(() => setBusy(false));
  };
  if (!s.quarantine?.active) return null;
  return (
    <div className="border border-red-300 bg-red-50/50 rounded-lg p-3" role="alert" data-testid="quarantine-block">
      <p className="text-[11px] font-bold text-red-800">Citation conflict — source quarantined</p>
      <p className="text-[10px] text-slate-600 mt-0.5">Removed from approved synthesis; linked claims reopened; affected report sections blocked until resolved. Conflicting fields: {(s.quarantine.conflicting_fields || []).join(", ")}.</p>
      {cmp && (
        <table className="w-full text-[10.5px] mt-2" aria-label="Compare metadata — displayed vs authoritative">
          <thead><tr className="text-left border-b border-red-200">
            {["Field", "Displayed", "Authoritative"].map((h) => <th key={h} scope="col" className="py-1 pr-2 font-mono text-[9px] uppercase text-slate-400">{h}</th>)}</tr></thead>
          <tbody>{cmp.fields.map((f) => (
            <tr key={f.field} className={f.conflict ? "text-red-700 font-semibold" : "text-slate-600"} data-testid={`conflict-field-${f.field}`}>
              <td className="py-1 pr-2">{f.field}{f.conflict && " ⚠"}</td><td className="py-1 pr-2">{String(f.displayed ?? "—")}</td><td className="py-1 pr-2">{String(f.authoritative ?? "—")}</td>
            </tr>))}</tbody>
        </table>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <Input placeholder="Resolution reason (required)" value={reason} onChange={(e) => setReason(e.target.value)}
          aria-label="Conflict resolution reason" className="h-7 text-[11px] flex-1 min-w-[180px]" data-testid="resolve-reason-input" />
        <Button variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={resolve} data-testid="resolve-conflict-btn"
          aria-label={`Resolve conflict for ${s.id} using the authoritative record`}>
          {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}Resolve — apply authoritative values</Button>
      </div>
    </div>
  );
}

function CascadeTrigger({ s, onUpdated }) {
  const [event, setEvent] = useState("");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [graph, setGraph] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/cascade/graph/${s.id}`).then((r) => setGraph(r.data)).catch(() => {}); }, [s.id]);
  const active = s.correction_event && !s.correction_event.resolved;
  const trigger = () => {
    if (!event) { toast.error("Select the correction event type first."); return; }
    if (reason.trim().length < 5) {
      setReasonError("A reason of at least 5 characters is required to trigger a cascade.");
      document.querySelector('[data-testid="cascade-reason-input"]')?.focus();
      return;
    }
    setBusy(true);
    api.post("/cascade/trigger", { source_id: s.id, event, reason: reason.trim() })
      .then((r) => {
        toast.success(`${r.data.event_id}: ${r.data.total_flags} dependents marked Stale`
          + (r.data.recalled_reports.length ? ` — ${r.data.recalled_reports.map((x) => `${x.report_id} ${x.recall_status.toLowerCase()}`).join(", ")}` : ""));
        onUpdated({ ...s, correction_event: { event_id: r.data.event_id, event, reason, resolved: false, recall_status: r.data.recall_status } });
        setEvent(""); setReason("");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Cascade trigger failed — please retry.",
        { action: { label: "Retry", onClick: trigger } }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="cascade-trigger-panel" aria-busy={busy}>
      <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Correction &amp; recall cascade</p>
      {active && (
        <p className="text-[10.5px] text-red-700 font-semibold mb-1.5" data-testid="cascade-active-badge">
          Active correction event {s.correction_event.event_id} ({s.correction_event.event}) — dependents Stale until reviewed on the Correction Cascade console.
        </p>
      )}
      {graph && (
        <p className="text-[10px] text-slate-500 mb-1.5" data-testid="cascade-impact-preview">
          Dependency graph: {graph.total_dependents} dependent objects — {graph.tiers.filter((t) => t.objects.length).map((t) => `${t.objects.length} ${t.name.toLowerCase()}`).join(" · ")}.
        </p>
      )}
      {!active && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <Select value={event} onValueChange={setEvent}>
              <SelectTrigger className="h-7 w-40 text-[11px]" data-testid="cascade-event-select" aria-label="Correction event type">
                <SelectValue placeholder="Event type…" /></SelectTrigger>
              <SelectContent>
                {["corrected", "withdrawn", "retracted", "downgraded"].map((e) => (
                  <SelectItem key={e} value={e} className="text-xs">{e[0].toUpperCase() + e.slice(1)}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input placeholder="Reason (required)" value={reason} onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(""); }}
              aria-label="Reason for correction event" aria-invalid={!!reasonError}
              aria-describedby={reasonError ? "cascade-reason-error" : undefined}
              className={`h-7 text-[11px] flex-1 min-w-[180px] ${reasonError ? "border-red-300" : ""}`} data-testid="cascade-reason-input" />
            <Button variant="outline" className="h-7 text-[10.5px] text-red-700 border-red-300" disabled={busy}
              onClick={trigger} data-testid="cascade-trigger-btn" aria-label={`Trigger correction cascade for ${s.id}`}>
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}{busy ? "Cascading…" : "Trigger cascade"}
            </Button>
          </div>
          {reasonError && <p id="cascade-reason-error" className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid="cascade-reason-error">{reasonError}</p>}
        </>
      )}
    </div>
  );
}

function LifecyclePanel({ s, onUpdated, onDeleted }) {
  const { isAdmin } = useRole();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const call = (fn, ok) => {
    setBusy(true);
    fn().then(ok).catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Lifecycle action failed."))
      .finally(() => setBusy(false));
  };
  const refetch = () => api.get("/source-library?include_archived=true").then((r) => {
    const upd = r.data.sources.find((x) => x.id === s.id);
    if (upd) onUpdated(upd);
  });
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="lifecycle-panel">
      <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Lifecycle — archive, restore, retention-gated deletion</p>
      {s.archived ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-[10.5px] text-amber-800" data-testid="archived-banner">
          Archived {s.archived.at?.slice(0, 10)} by {s.archived.name || s.archived.by}{s.archived.reason ? ` — ${s.archived.reason}` : ""}. Excluded from all approved outputs until restored.
        </div>
      ) : (
        <Input placeholder="Archive reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
          aria-label="Archive reason" className="h-8 text-xs" data-testid="archive-reason-input" />
      )}
      <div className="flex flex-wrap gap-1.5">
        {!s.archived && (
          <Button variant="outline" className="h-7 text-[11px]" disabled={busy} data-testid="archive-source-btn"
            onClick={() => call(() => api.post(`/source-library/${s.id}/archive`, { reason }), refetch)}>Archive</Button>
        )}
        {s.archived && (
          <Button variant="outline" className="h-7 text-[11px]" disabled={busy} data-testid="restore-source-btn"
            onClick={() => call(() => api.post(`/source-library/${s.id}/restore`, {}), refetch)}>Restore</Button>
        )}
        {isAdmin && (
          <Button variant="outline" className="h-7 text-[11px] text-red-700 border-red-200" disabled={busy} data-testid="delete-source-btn"
            onClick={() => call(() => api.delete(`/source-library/${s.id}`), () => { toast.success("Source permanently deleted."); onDeleted?.(s.id); })}>
            Permanently delete (admin · retention-gated)</Button>
        )}
      </div>
      <p className="text-[10px] text-slate-400">Draft and sandbox records only — approved sources cannot be archived. Permanent deletion is admin-only and requires the 30-day retention period after archiving.</p>
    </div>
  );
}


function ReviewTab({ s, onUpdated }) {
  const { role, canReview } = useRole();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [busy, setBusy] = useState(false);
  const run = (action, extra = {}) => {
    if (action === "exclude" && !reason.trim()) {
      setReasonError("A reason is required to exclude a source from synthesis.");
      document.querySelector('[data-testid="review-reason-input"]')?.focus();
      return;
    }
    setBusy(true);
    api.patch(`/source-library/${s.id}/review`, { action, reason: reason || null, ...extra })
      .then((r) => {
        onUpdated(r.data.source);
        toast.success(`Action applied: ${action.replace(/_/g, " ")}`);
        if (r.data.downstream_flag) toast.warning(`Downstream objects flagged for reassessment: ${r.data.downstream_flag.flagged_insights.join(", ")}`);
        setReason("");
      })
      .catch((e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed — please retry.", { action: { label: "Retry", onClick: () => run(action, extra) } }))
      .finally(() => setBusy(false));
  };
  const btn = "h-7 text-[11px]";
  return (
    <div className="space-y-4" data-testid="source-review-tab" aria-busy={busy}>
      {busy && (
        <p className="text-[10.5px] text-slate-400 flex items-center gap-1.5" role="status" aria-live="polite" data-testid="review-action-loading">
          <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Applying review action…
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 text-[11.5px]">
        <Field l="Verification status" v={s.verification_status} />
        <Field l="Scientific-review status" v={s.sci_review_status} />
        <Field l="Assigned reviewer" v={s.reviewer} />
        <Field l="Last review date" v={s.last_review_date} />
      </div>
      {!canReview && (
        <p className="text-[10.5px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="review-role-note">
          Verification, review and approval actions require a Scientific Reviewer, Senior Reviewer or Administrator role. Your role: {ROLE_LABEL[role] || role || "—"}. The server enforces this independently of the interface.
        </p>
      )}
      {canReview && (<>
      <div>
        <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1.5">Verification actions</p>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="outline" className={btn} disabled={busy} onClick={() => run("verify_source")} data-testid="review-verify-source">Verify source</Button>
          <Button variant="outline" className={btn} disabled={busy} onClick={() => run("mark_partially_verified")}>Partially verified</Button>
          <Button variant="outline" className={`${btn} text-red-700 border-red-200`} disabled={busy} onClick={() => run("fail_verification")}>Fail verification</Button>
        </div>
      </div>
      <div>
        <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1.5">Scientific-review actions</p>
        <div className="flex flex-wrap gap-1.5">
          <Button variant="outline" className={btn} disabled={busy} onClick={() => run("start_review")}>Start review</Button>
          <Button variant="outline" className={btn} disabled={busy} onClick={() => run("revision_required")}>Revision required</Button>
          <Button variant="outline" className={btn} disabled={busy} onClick={() => run("mark_reviewed")}>Mark reviewed</Button>
          <Button className={`${btn} bg-teal-600 hover:bg-teal-700 text-white`} disabled={busy} onClick={() => run("approve_synthesis")} data-testid="review-approve-synthesis">Approve for synthesis</Button>
          <Button variant="outline" className={`${btn} text-red-700 border-red-200`} disabled={busy} onClick={() => run("exclude")} data-testid="review-exclude">Exclude (reason required)</Button>
        </div>
      </div>
      </>)}
      <div>
        <Input placeholder="Reason / rationale (required for exclusion)" value={reason} onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(""); }}
          aria-label="Reason or rationale for review action" aria-invalid={!!reasonError} aria-describedby={reasonError ? "review-reason-error" : undefined}
          className={`h-8 text-xs ${reasonError ? "border-red-300" : ""}`} data-testid="review-reason-input" />
        {reasonError && <p id="review-reason-error" className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid="review-reason-error">{reasonError}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Input placeholder="Assign reviewer (e.g., Example MarksMan reviewer C)" value={reviewer} onChange={(e) => setReviewer(e.target.value)}
          aria-label="Reviewer name to assign" className="h-8 text-xs" data-testid="review-assign-input" />
        <Button variant="outline" className={btn} disabled={busy || !reviewer.trim()} onClick={() => run("assign_reviewer", { reviewer })} data-testid="review-assign-btn" aria-label="Assign reviewer to this source">Assign</Button>
      </div>
      <QuarantineBlock s={s} onUpdated={onUpdated} />
      <CascadeTrigger s={s} onUpdated={onUpdated} />
      <LifecyclePanel s={s} onUpdated={onUpdated} />
      <p className="text-[10px] text-slate-400 flex items-start gap-1"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5 text-amber-500" aria-hidden="true" />
        Approval for synthesis requires the source to be Source verified. Excluding or failing a source flags every downstream insight for reassessment — nothing is silently preserved.</p>
    </div>
  );
}

export default function SourceDetail({ source, onClose, onUpdated, onOpenInsight }) {
  const [tab, setTab] = useState("Overview");
  const s = source;
  if (!s) return null;
  const isEvidence = (s.claims || []).length > 0 || ["Peer-reviewed publication", "Congress abstract", "Congress poster", "Targeted literature review", "Systematic literature review"].includes(s.source_type);
  const dl = () => {
    api.get(`/source-library/${s.id}/document`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = s.document.original_filename; a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error("Download failed — please retry.", { action: { label: "Retry", onClick: dl } }));
  };
  const canDownload = (s.permissions?.download || "").startsWith("Permitted");

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="source-detail" aria-label={`Source detail — ${s.id}`}>
        <SheetHeader className="text-left">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{s.id}</span>
            <Badge variant="outline" className={`text-[9px] ${VERIF_BG[s.verification_status]}`}>{s.verification_status}</Badge>
            <Badge variant="outline" className={`text-[9px] ${SCIREV_BG[s.sci_review_status]}`}>{s.sci_review_status}</Badge>
            <span className="text-[9px] font-mono uppercase text-amber-600/80 ml-auto">Illustrative demonstration data</span>
          </div>
          <SheetTitle className="font-heading text-base leading-snug">{s.title}</SheetTitle>
          <SheetDescription className="sr-only">
            Detailed record for source {s.id} including document metadata, extracted claims, HCP statements, evidence assessment, relationships, review actions and audit history.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-wrap gap-1 mt-3 mb-4" role="tablist" aria-label="Source detail sections">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`detail-tab-${t.toLowerCase().replace(/ /g, "-")}`}
              role="tab" aria-selected={tab === t} aria-label={`${t} tab`}
              className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border ${tab === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-teal-400"}`}>{t}</button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-3" data-testid="detail-overview">
            <Field l="Author / speaker" v={s.author_speaker} />
            <Field l="Organisation" v={s.organisation} />
            <Field l="Source type" v={s.source_type} />
            <Field l="Provenance" v={s.provenance} />
            <Field l="Publication / observation date" v={s.date} />
            <Field l="Date added" v={s.date_added} />
            <Field l="Geography" v={s.geography} />
            <Field l="Journal / congress" v={s.conference_journal} />
            <Field l="Language" v={s.language} />
            <Field l="DOI" v={s.identifiers?.doi} />
            <Field l="PMID / NCT / Abstract no." v={[s.identifiers?.pmid, s.identifiers?.nct, s.identifiers?.abstract_no].filter(Boolean).join(" · ") || "—"} />
            <Field l="Access classification" v={s.access_classification} />
            <Field l="Cut-off eligibility" v={s.cutoff_status} />
            <Field l="Current version" v={`v${s.doc_version || 1}`} />
            <Field l="Assigned reviewer" v={s.reviewer} />
            <Field l="Evidence family" v={s.evidence_family?.name} />
            <div className="col-span-2"><Field l="Copyright / usage note" v={s.copyright_note} /></div>
            {s.evidence_summary && <div className="col-span-2"><Field l="Evidence summary" v={s.evidence_summary} /></div>}
          </div>
        )}

        {tab === "Document" && (
          <div className="space-y-3" data-testid="detail-document">
            {s.document ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><FileText className="w-4 h-4 text-teal-600" />{s.document.original_filename} <span className="text-slate-400 font-normal">({(s.document.size / 1024).toFixed(0)} KB)</span></p>
                {canDownload ? (
                  <Button variant="outline" className="h-7 text-[11px] mt-2" onClick={dl} data-testid="detail-doc-download"><Download className="w-3 h-3 mr-1" />Download</Button>
                ) : (
                  <p className="text-[10.5px] text-amber-700 mt-2">Download restricted — {s.permissions?.download}. Metadata and approved extracted summaries remain available.</p>
                )}
              </div>
            ) : s.url ? (
              <a href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-teal-700 font-medium" data-testid="detail-doc-url"><ExternalLink className="w-3.5 h-3.5" />Open legitimate original source</a>
            ) : (
              <p className="text-xs text-slate-400">No document or external link on record.</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field l="Quotation permission" v={s.permissions?.quotation} />
              <Field l="Redistribution" v={s.permissions?.redistribution} />
              <Field l="Confidentiality" v={s.permissions?.confidentiality} />
              <Field l="Consent basis" v={s.permissions?.consent_basis} />
            </div>
            <Field l="Version history" v={(s.version_history || []).length ? s.version_history.map((v) => v.note).join("; ") : "v1 — no prior versions"} />
            <p className="text-[10px] text-slate-400">Secure preview, in-source search and excerpt anchors activate when a permitted full-text document is attached. Restricted content is never displayed or distributed without permission.</p>
          </div>
        )}

        {tab === "Extracted Claims" && (
          <div className="space-y-2" data-testid="detail-claims">
            {(s.claims || []).map((c) => (
              <div key={c.claim_id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-[11.5px] text-slate-700 leading-snug">{c.text}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
                  <Field l="Claim ID" v={c.claim_id} />
                  <Field l="Location" v={c.location} />
                  <Field l="Claim type" v={c.claim_type} />
                  <Field l="Clinical theme" v={c.theme} />
                  <Field l="Population" v={c.population} />
                  <Field l="AI extraction confidence" v={c.ai_confidence} />
                  <Field l="Human verification" v={c.verification} />
                  <Field l="Linked insight" v={c.linked_insight} />
                </div>
              </div>
            ))}
            {(s.claims || []).length === 0 && <p className="text-xs text-slate-400">No extracted claims — this source contributes HCP statements or contextual intelligence rather than evidence claims.</p>}
          </div>
        )}

        {tab === "HCP Statements" && (
          <div className="space-y-2" data-testid="detail-statements">
            {(s.hcp_statements || []).map((st) => (
              <div key={st.statement_id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-[11.5px] text-slate-700 leading-snug">{st.text}</p>
                <p className="text-[9.5px] text-violet-600 mt-1">{st.content_type}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
                  <Field l="Statement ID" v={st.statement_id} />
                  <Field l="Attribution" v={st.attribution} />
                  <Field l="Specialty / geography" v={`${st.specialty} · ${st.geography}`} />
                  <Field l="Source location" v={st.location} />
                  <Field l="Anonymisation" v={st.anonymisation} />
                  <Field l="Quotation permission" v={st.quotation_permission} />
                  <Field l="Verification" v={st.verification} />
                  <Field l="Linked insight" v={st.linked_insight} />
                </div>
              </div>
            ))}
            {(s.hcp_statements || []).length === 0 && <p className="text-xs text-slate-400">No HCP statements extracted from this source.</p>}
          </div>
        )}

        {tab === "Evidence Assessment" && (
          <div className="space-y-3" data-testid="detail-assessment">
            <div className="grid grid-cols-2 gap-3">
              <Field l="Evidence-quality assessment" v={s.evidence_quality} />
              <Field l="Framework used" v={s.quality_framework || "—"} />
            </div>
            <Field l="Reviewer rationale" v={s.quality_rationale} />
            {!isEvidence && <p className="text-[10px] text-amber-700">Non-evidence source — study-level quality ratings are not assigned; a source-specific credibility framework applies.</p>}
            {s.evidence_summary && <Field l="Key findings (summary)" v={s.evidence_summary} />}
            <Field l="Geographic applicability" v={s.geography} />
          </div>
        )}

        {tab === "Relationships" && (
          <div className="space-y-3" data-testid="detail-relationships">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Evidence family</p>
              <p className="text-[11.5px] text-slate-700">{s.evidence_family?.name}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {(s.evidence_family?.members || []).map((m) => (
                  <span key={m} className={`font-mono text-[9.5px] border rounded px-1.5 py-0.5 ${m === s.id ? "bg-slate-900 text-teal-300 border-slate-900" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{m}</span>
                ))}
              </div>
              {(s.evidence_family?.members || []).length > 1 && <p className="text-[10px] text-slate-400 mt-1">Family members report the same underlying study and are not counted as independent evidence.</p>}
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Insights supported ({(s.linked_insights || []).length})</p>
              <div className="flex flex-wrap gap-1">
                {(s.linked_insights || []).map((i) => (
                  <button key={i} onClick={() => onOpenInsight && onOpenInsight(i)} className="font-mono text-[10px] text-teal-700 border border-teal-200 bg-teal-50 rounded px-1.5 py-0.5 hover:bg-teal-100" data-testid={`rel-insight-${i}`}>{i} →</button>
                ))}
                {(s.linked_insights || []).length === 0 && <span className="text-[11px] text-slate-400">None linked yet</span>}
              </div>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Clinical themes</p>
              <div className="flex flex-wrap gap-1">{(s.themes || []).map((t) => <span key={t} className="text-[10px] bg-teal-50 border border-teal-100 text-teal-700 rounded-full px-2 py-0.5">{t}</span>)}</div>
            </div>
            {s.downstream_flag && (
              <p className="text-[10.5px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{s.downstream_flag.note} Affected: {s.downstream_flag.flagged_insights.join(", ")}</p>
            )}
            <p className="text-[10px] text-slate-400">Relationships to consensus findings, controversies, emerging signals and reports resolve through the linked insights above; every link is navigable in both directions.</p>
          </div>
        )}

        {tab === "Review" && <ReviewTab s={s} onUpdated={onUpdated} />}

        {tab === "Audit History" && (
          <div className="space-y-1.5" data-testid="detail-audit">
            {(s.audit || []).slice().reverse().map((a, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-[11px] text-slate-600">
                <p><span className="font-mono text-[10px] text-slate-400 mr-2">{a.date}</span><span className="font-semibold text-slate-700">{a.user}</span> — {a.field}: <span className="text-slate-400">{a.from ?? "—"}</span> → <span className="font-semibold">{a.to}</span></p>
                <p className="text-[10px] text-slate-400 mt-0.5">Reason: {a.reason} · doc v{a.doc_version}</p>
              </div>
            ))}
            {(s.audit || []).length === 0 && <p className="text-xs text-slate-400">No audit entries.</p>}
          </div>
        )}

        <div className="mt-5 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400">Processing</span>
          {(s.processing_stages || []).map((p) => (
            <span key={p.stage} className={`flex items-center gap-1 text-[9.5px] ${p.status === "Complete" ? "text-teal-600" : p.status === "Not applicable" ? "text-slate-300" : "text-amber-600"}`}>
              {p.status === "Complete" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{p.stage}
            </span>
          ))}
          <span className="text-[9px] text-slate-400 w-full">"Processing complete" does not imply scientific verification.</span>
        </div>
      </SheetContent>
    </Sheet>
  );
}
